import React, { useCallback, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import { usePermissions } from "../../../../utils/permissions";
import PlantillasTabs from "../../catalogo/components/PlantillasTabs";
import PlantillaFormModal from "../components/PlantillaFormModal.jsx";
import {
  listarPlantillas,
  crearPlantilla,
  actualizarPlantilla,
  eliminarPlantilla,
} from "../../../../services/convocatoriaPlantillasService";
import { listarCentros } from "../../../../services/centrosService";
import { showToast } from "../../../../utils/showToast";

const EJEMPLO = {
  anio: "2026",
  numero_promocion: "0001",
  ciclo_codigo: "MDI-DRSP",
  centro_alias: "CENDCAP",
  centro_codigo_mdi: "M75",
  curso_codigo: "N1",
};

const LABEL_MAP = {
  anio:              "Año",
  numero_promocion:  "N° Promoción",
  ciclo_codigo:      "Código de Ciclo",
  centro_alias:      "Centro",
  centro_codigo_mdi: "Código MDI del Centro",
  curso_codigo:      "Código del Curso",
};

function buildPreview(campos, separador) {
  const sep = separador || "-";
  if (!Array.isArray(campos) || !campos.length) return "—";
  return campos
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map((c) => EJEMPLO[c.campo] || c.campo)
    .join(sep);
}

const formatDate = (value) => {
  if (!value) return "-";
  const parts = String(value).split("T")[0].split("-");
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const initialForm = {
  centro_id: "",
  nombre: "",
  separador: "-",
  campos: [],
  vigente_desde: "",
  activo: 1,
};

export default function ConvocatoriaPlantillasView() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("convocatoria_plantillas.crear");
  const canUpdate = hasPermission("convocatoria_plantillas.actualizar");
  const canDelete = hasPermission("convocatoria_plantillas.eliminar");

  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [centros, setCentros] = useState([]);
  const [centrosLoading, setCentrosLoading] = useState(true);

  const [modal, setModal] = useState(""); // "nuevo" | "editar" | "eliminar"
  const [seleccionada, setSeleccionada] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cargarPlantillas = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const data = await listarPlantillas();
      setPlantillas(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError(err.message || "No se pudieron cargar las plantillas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPlantillas();
    listarCentros()
      .then((data) => setCentros(Array.isArray(data) ? data : []))
      .catch(() => setCentros([]))
      .finally(() => setCentrosLoading(false));
  }, [cargarPlantillas]);

  const abrirNuevo = () => {
    setForm(initialForm);
    setSeleccionada(null);
    setModal("nuevo");
  };

  const abrirEditar = (plantilla) => {
    const campos = typeof plantilla.campos === "string"
      ? JSON.parse(plantilla.campos)
      : plantilla.campos || [];
    setForm({
      centro_id: String(plantilla.centro_id || ""),
      nombre: plantilla.nombre || "",
      separador: plantilla.separador || "-",
      campos,
      vigente_desde: plantilla.vigente_desde
        ? String(plantilla.vigente_desde).split("T")[0]
        : "",
      activo: plantilla.activo ?? 1,
    });
    setSeleccionada(plantilla);
    setModal("editar");
  };

  const abrirEliminar = (plantilla) => {
    setSeleccionada(plantilla);
    setModal("eliminar");
  };

  const cerrarModal = () => {
    setModal("");
    setSeleccionada(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.centro_id) { showToast({ type: "warning", message: "Selecciona el centro." }); return; }
    if (!form.nombre?.trim()) { showToast({ type: "warning", message: "El nombre es obligatorio." }); return; }
    if (!form.vigente_desde) { showToast({ type: "warning", message: "La fecha de vigencia es obligatoria." }); return; }
    if (!form.campos?.length) { showToast({ type: "warning", message: "Agrega al menos un campo al formato." }); return; }

    setSaving(true);
    try {
      const payload = {
        centro_id: Number(form.centro_id),
        nombre: form.nombre.trim(),
        separador: form.separador || "-",
        campos: form.campos,
        vigente_desde: form.vigente_desde,
        activo: form.activo ?? 1,
      };

      if (modal === "nuevo") {
        await crearPlantilla(payload);
        showToast({ type: "success", message: "Plantilla creada correctamente." });
      } else {
        await actualizarPlantilla(seleccionada.id, payload);
        showToast({ type: "success", message: "Plantilla actualizada correctamente." });
      }
      cerrarModal();
      cargarPlantillas();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo guardar la plantilla." });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async () => {
    if (!seleccionada) return;
    setDeleting(true);
    try {
      await eliminarPlantilla(seleccionada.id);
      showToast({ type: "success", message: "Plantilla desactivada correctamente." });
      cerrarModal();
      cargarPlantillas();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo desactivar la plantilla." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="management-page management-theme-violet">
      <PlantillasTabs />

      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Configuracion academica</p>
          <h4 className="management-title mb-1">Plantillas de convocatoria</h4>
          <p className="text-muted mb-0">
            Define el formato del codigo de convocatoria (codigo MDI) que se generara
            automaticamente al crear una promocion.
          </p>
        </div>
        <div>
          {canCreate && (
            <button type="button" className="btn management-btn-primary" onClick={abrirNuevo}>
              <i className="bi bi-plus-circle me-2" />
              Nueva plantilla
            </button>
          )}
        </div>
      </div>

      {listError && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center mb-4">
          <span>{listError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cargarPlantillas}>
            Reintentar
          </button>
        </div>
      )}

      <div className="management-card">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : plantillas.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-layout-text-sidebar-reverse fs-1 d-block mb-2" />
            No hay plantillas configuradas.
            {canCreate && (
              <div className="mt-2">
                <button type="button" className="btn btn-sm management-btn-primary" onClick={abrirNuevo}>
                  Crear primera plantilla
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Centro</th>
                  <th>Nombre</th>
                  <th>Vigente desde</th>
                  <th>Formato (ejemplo)</th>
                  <th>Estado</th>
                  {(canUpdate || canDelete) && <th className="text-end">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {plantillas.map((p) => {
                  const campos = typeof p.campos === "string" ? JSON.parse(p.campos) : p.campos || [];
                  return (
                    <tr key={p.id}>
                      <td className="small">{p.centro_nombre || `Centro ${p.centro_id}`}</td>
                      <td className="fw-semibold">{p.nombre}</td>
                      <td className="small">{formatDate(p.vigente_desde)}</td>
                      <td>
                        <code className="small">{buildPreview(campos, p.separador)}</code>
                        <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                          {campos
                            .slice()
                            .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
                            .map((c) => LABEL_MAP[c.campo] || c.label || c.campo)
                            .join(` ${p.separador || "-"} `)}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${p.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-muted"}`}>
                          {p.activo ? "Activa" : "Inactiva"}
                        </span>
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => abrirEditar(p)}
                              >
                                <i className="bi bi-pencil" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => abrirEliminar(p)}
                              >
                                <i className="bi bi-slash-circle" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PlantillaFormModal
        open={modal === "nuevo" || modal === "editar"}
        title={modal === "nuevo" ? "Nueva plantilla de convocatoria" : "Editar plantilla"}
        form={form}
        setForm={setForm}
        saving={saving}
        onClose={cerrarModal}
        onSubmit={handleSubmit}
        centros={centros}
        centrosLoading={centrosLoading}
      />

      {/* Modal eliminar */}
      {modal === "eliminar" && seleccionada && (
        <div
          className="modal fade show d-block management-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Desactivar plantilla</h5>
                <button type="button" className="btn-close" onClick={cerrarModal} />
              </div>
              <div className="modal-body pt-0">
                <p className="mb-1">
                  ¿Desactivar la plantilla <strong>{seleccionada.nombre}</strong>?
                </p>
                <p className="text-muted small mb-0">
                  Las promociones ya creadas conservan su codigo. Solo afecta a nuevas promociones.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleEliminar}
                  disabled={deleting}
                >
                  {deleting ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Desactivando...</>
                  ) : (
                    "Desactivar"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
